// Client of the live-log WebSocket, protocol peak.logs.v1 (api/logs-protocol.md, track L).
// The client names (environment, action) only (AD-10). Browsers cannot set headers on a WebSocket, so the
// token travels as the subprotocol `bearer.<token>` next to `peak.logs.v1`.
// AD-14: the server re-authorises every 60 s and closes 4408 on lapse; the client renews its token and
// reconnects. 4401/4403/4404/4500 end the stream with a sentence the view shows.
import type { TokenSource } from '../auth/oidc';

export const PROTOCOL = 'peak.logs.v1';

export interface LogCondition { type: string; status: string; reason: string; message: string; lastTransitionTime: string }
export interface LogLastAction { id: string; type: string; outcome: string; reason: string; message: string; finishedAt: string }
export interface Summary { phase: string | null; conditions: LogCondition[]; lastAction: LogLastAction | null }

export type Frame =
  | { type: 'ready'; protocol: string; environment: string; action: string; reauthorizeSeconds: number }
  | { type: 'job'; job: string; step: string }
  | { type: 'pod'; pod: string; attempt: number }
  | { type: 'container'; container: string; init: boolean }
  | { type: 'line'; container: string; ts: string | null; text: string; truncated?: boolean }
  | { type: 'job-end'; job: string; step: string; result: 'Succeeded' | 'Failed' | 'Unknown' | 'Gone'; reason?: string; exitCode?: number | null; container?: string; message?: string }
  | { type: 'waiting'; phase: string }
  | ({ type: 'idle'; environment: string; action: string } & Summary)
  | ({ type: 'end'; environment: string; action: string } & Summary);

export type StreamState = 'connecting' | 'open' | 'reconnecting' | 'ended' | 'failed' | 'stopped';

export type LogEvent =
  | { kind: 'frame'; frame: Frame }
  | { kind: 'state'; state: StreamState; sentence?: string; code?: number };

/** The parts of the browser WebSocket the client uses (injectable for tests). */
export interface SocketLike {
  readonly protocol: string;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  close(code?: number, reason?: string): void;
}
export type SocketFactory = (url: string, protocols: string[]) => SocketLike;

export interface LogStreamOptions {
  /** ws(s)://…/api/v1 */
  base: string;
  environment: string;
  /** `current` (the default) or a step name from the protocol. */
  action?: string;
  auth: TokenSource;
  onEvent: (e: LogEvent) => void;
  socket?: SocketFactory;
  /** Backoff for network drops and 4429/4500/1001; 4408 reconnects at once. */
  backoffMs?: (attempt: number) => number;
  /** Consecutive failed reconnects before giving up. */
  maxRetries?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

/** Sentences for the close codes (AD-13/AD-14 vocabulary). */
export function closeSentence(code: number, reason: string, action: string): string {
  switch (code) {
    case 1000: return 'The stream ended.';
    case 1001: return 'The server is restarting; reconnecting.';
    case 4401: return 'The log stream did not accept your login (missing, expired or untrusted token). Log in again to see the live log.';
    case 4403: return 'You are not entitled to this environment\'s customer, or the environment does not exist.';
    case 4404: return `The log stream does not know the step "${action}".`;
    case 4408: return 'Your authorization lapsed (it is re-checked every 60 s); reconnecting with a fresh login.';
    case 4429: return 'The browser read the log too slowly and the server dropped the connection; reconnecting.';
    case 4500: return `An upstream service (Kubernetes API or the entitlement source) failed${reason ? `: ${reason}` : ''}.`;
    default: return `The connection closed (code ${code}${reason ? `, ${reason}` : ''}).`;
  }
}

const FINAL_CODES = new Set([4403, 4404]);
const RETRY_CODES = new Set([1001, 1006, 4429, 4500]);

export class LogStream {
  private ws?: SocketLike;
  private stopped = false;
  private ended = false;
  private retries = 0;
  private authRetried = false;
  private timer: unknown;
  private readonly o: Required<Pick<LogStreamOptions, 'action' | 'maxRetries'>> & LogStreamOptions;

  constructor(opts: LogStreamOptions) {
    this.o = { action: 'current', maxRetries: 5, ...opts };
  }

  get url(): string {
    return `${this.o.base.replace(/\/+$/, '')}/environments/${encodeURIComponent(this.o.environment)}/logs?action=${encodeURIComponent(this.o.action)}`;
  }

  start(): void {
    this.stopped = false;
    this.ended = false;
    void this.connect(false);
  }

  stop(): void {
    this.stopped = true;
    this.clear();
    const ws = this.ws;
    this.ws = undefined;
    if (ws) {
      ws.onclose = null;
      ws.close(1000, 'closed by the viewer');
    }
    this.emit({ kind: 'state', state: 'stopped' });
  }

  private emit(e: LogEvent): void {
    this.o.onEvent(e);
  }

  private clear(): void {
    if (this.timer !== undefined) (this.o.clearTimer ?? clearTimeout)(this.timer as ReturnType<typeof setTimeout>);
    this.timer = undefined;
  }

  private async connect(renew: boolean): Promise<void> {
    if (this.stopped) return;
    this.emit({ kind: 'state', state: this.retries || renew ? 'reconnecting' : 'connecting' });
    let token: string;
    try {
      token = renew ? await this.o.auth.renew() : await this.o.auth.token();
    } catch (e) {
      this.emit({ kind: 'state', state: 'failed', sentence: `Could not obtain a token for the log stream: ${(e as Error).message}` });
      return;
    }
    if (this.stopped) return;
    const factory: SocketFactory = this.o.socket ?? ((u, p) => new WebSocket(u, p) as unknown as SocketLike);
    const ws = factory(this.url, [PROTOCOL, `bearer.${token}`]);
    this.ws = ws;
    ws.onmessage = (ev) => this.onMessage(ev.data);
    ws.onclose = (ev) => this.onClose(ws, ev.code, ev.reason);
    ws.onerror = () => {};
  }

  private onMessage(data: unknown): void {
    if (typeof data !== 'string') return; // v1 sends text frames only
    let frame: Frame;
    try {
      frame = JSON.parse(data) as Frame;
    } catch {
      return;
    }
    if (!frame || typeof frame !== 'object' || typeof frame.type !== 'string') return;
    if (frame.type === 'ready') {
      this.retries = 0;
      this.authRetried = false;
      this.emit({ kind: 'state', state: 'open' });
    }
    if (frame.type === 'end' || frame.type === 'idle') this.ended = true;
    this.emit({ kind: 'frame', frame });
  }

  private onClose(ws: SocketLike, code: number, reason: string): void {
    if (ws !== this.ws || this.stopped) return;
    this.ws = undefined;
    const sentence = closeSentence(code, reason, this.o.action);

    if (code === 1000 || this.ended) {
      this.emit({ kind: 'state', state: 'ended', code, sentence: this.ended ? 'The stream ended.' : sentence });
      return;
    }
    if (code === 4408) {
      // AD-14: never drained, re-authenticate and come back.
      this.emit({ kind: 'state', state: 'reconnecting', code, sentence });
      if (++this.retries > this.o.maxRetries) return this.fail(code, sentence);
      void this.connect(true);
      return;
    }
    if (code === 4401) {
      if (!this.authRetried) {
        this.authRetried = true;
        this.emit({ kind: 'state', state: 'reconnecting', code, sentence: 'The log stream refused the token; retrying once with a fresh login.' });
        void this.connect(true);
        return;
      }
      return this.fail(code, sentence);
    }
    if (FINAL_CODES.has(code)) return this.fail(code, sentence);
    if (RETRY_CODES.has(code) || code < 4000) {
      if (++this.retries > this.o.maxRetries) return this.fail(code, `${sentence} Gave up after ${this.o.maxRetries} attempts.`);
      this.emit({ kind: 'state', state: 'reconnecting', code, sentence });
      const delay = (this.o.backoffMs ?? ((n) => Math.min(30_000, 1000 * 2 ** (n - 1))))(this.retries);
      this.timer = (this.o.setTimer ?? setTimeout)(() => void this.connect(false), delay);
      return;
    }
    this.fail(code, sentence);
  }

  private fail(code: number, sentence: string): void {
    this.emit({ kind: 'state', state: 'failed', code, sentence });
  }
}
